import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { formatErrorMessage } from '../utils/errorHandler';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Refresh access token 5 minutes before expiry
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

function parseTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : 0; // ms
  } catch {
    return 0;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Schedule silent refresh before access token expires
  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const expiry = parseTokenExpiry(accessToken);
    if (!expiry) return;

    const delay = Math.max(0, expiry - Date.now() - REFRESH_MARGIN_MS);

    refreshTimerRef.current = setTimeout(async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        clearSession();
        return;
      }
      try {
        const response = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
        const { access_token } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        scheduleRefresh(access_token);
      } catch {
        clearSession();
      }
    }, delay);
  }, [clearSession]);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
      scheduleRefresh(token);
    } else {
      setLoading(false);
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Multi-tab sync: logout in one tab → logout everywhere
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        clearSession();
      } else if (e.key === 'token' && e.newValue) {
        setToken(e.newValue);
        axios.defaults.headers.common['Authorization'] = `Bearer ${e.newValue}`;
        fetchCurrentUser();
        scheduleRefresh(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [clearSession, scheduleRefresh]);

  // Axios interceptor: auto-logout on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          // Token expired or revoked — try refresh
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken && !error.config._retry) {
            error.config._retry = true;
            return axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
              .then((res) => {
                const { access_token } = res.data;
                localStorage.setItem('token', access_token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
                setToken(access_token);
                scheduleRefresh(access_token);
                error.config.headers['Authorization'] = `Bearer ${access_token}`;
                return axios(error.config);
              })
              .catch(() => {
                clearSession();
                return Promise.reject(error);
              });
          }
          clearSession();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, clearSession, scheduleRefresh]);

  const fetchCurrentUser = async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, extraHeaders = {}) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { headers: extraHeaders });
      const { access_token, refresh_token, force_password_change, user_id, role, full_name } = response.data;
      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);

      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      if (!force_password_change) {
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        setUser(userResponse.data);
      }

      setToken(access_token);
      scheduleRefresh(access_token);

      return {
        success: true,
        force_password_change,
        user_id,
        role,
        full_name
      };
    } catch (error) {
      return {
        success: false,
        error: formatErrorMessage(error)
      };
    }
  };

  const googleLogin = async (idToken) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { id_token: idToken });
      const { access_token, refresh_token, force_password_change, user_id, role, full_name } = response.data;
      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      if (!force_password_change) {
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        setUser(userResponse.data);
      }

      setToken(access_token);
      scheduleRefresh(access_token);

      return {
        success: true,
        force_password_change,
        user_id,
        role,
        full_name
      };
    } catch (error) {
      return {
        success: false,
        error: formatErrorMessage(error)
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatErrorMessage(error)
      };
    }
  };

  const logout = async () => {
    // Server-side token invalidation
    try {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        await axios.post(`${API}/auth/logout`, null, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
      }
    } catch {
      // Logout succeeds client-side even if server call fails
    }
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, googleLogin, register, logout, loading, refreshUser: fetchCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
